import React, { useEffect, useRef, useState } from "react";
import PostsSection from "./PostSection";
import AboutSection from "./AboutSection";
import MediaSection from "./MediaSection";
import SavedSection from "./SavedSection";
import { api } from "../../actions/api";
import {
  useGetUserPosts,
  useGetScheduledPosts,
} from "../../Controllers/Tribe/TribePostController";

// Helper function to format date in user's timezone
const formatMemberSinceDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const TribeContent = ({
  selectedGroup,
  isAdminOnly,
  isPost,
  groupDetails,
  userProfile,
  isCurrentUserAdmin,
  onShowIndividualPostChange,
  onPost,
  selectedPostId,
  // selectedCommentId,
  // openCommentBox
  handleGroupClicked,
  memberProfileRequest,
  onMemberProfileRequestHandled,
}) => {
  const [activeTab, setActiveTab] = useState("posts");
  const [localGroupDetails, setLocalGroupDetails] = useState(groupDetails);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [showIndividualPost, setShowIndividualPost] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [loadingUserPosts, setLoadingUserPosts] = useState(false);
  const [currentSelectedUserId, setCurrentSelectedUserId] = useState(null);
  const [currentSelectedGroupId, setCurrentSelectedGroupId] = useState(null);
  const [showPostOwnerInfo, setShowPostOwnerInfo] = useState(false);
  const [postOwnerInfo, setPostOwnerInfo] = useState(null);
  const [useNewApiForUserPosts, setUseNewApiForUserPosts] = useState(false);

  // Use the new hook for fetching user posts when username is clicked (showPostOwnerInfoFlag = true)
  const {
    data: userPostsData,
    isLoading: isLoadingUserPostsFromHook,
    fetchNextPage: fetchNextUserPostsPage,
    hasNextPage: hasNextUserPostsPage,
    isFetchingNextPage: isFetchingNextUserPostsPage,
  } = useGetUserPosts(
    currentSelectedUserId,
    useNewApiForUserPosts && !!currentSelectedUserId && showIndividualPost,
  );

  // Scheduled posts:
  // - Feed (selectedGroup === 0): fetch ALL scheduled posts (no group_id)
  // - Group selected: fetch scheduled posts for that group_id
  const scheduledGroupId =
    typeof selectedGroup === "number" && selectedGroup !== 0
      ? selectedGroup
      : undefined;
  const isScheduledTabActive = activeTab === "scheduled";

  const {
    data: scheduledPostsData,
    isLoading: isLoadingScheduledPosts,
    fetchNextPage: fetchNextScheduledPostsPage,
    hasNextPage: hasNextScheduledPostsPage,
    isFetchingNextPage: isFetchingNextScheduledPostsPage,
  } = useGetScheduledPosts({
    groupId: scheduledGroupId,
    enabled: isScheduledTabActive,
  });

  const scheduledPosts = scheduledPostsData?.pages
    ? scheduledPostsData.pages.flatMap((p) => p.posts || [])
    : [];

  // Scrolling to specific post
  const hasScrolledToPost = useRef(false);
  const scrollContainerRef = useRef(null);

  const loadMorePosts = async () => {
    const postsMeta = localGroupDetails.meta.posts;
    if (loadingPosts || postsMeta?.current_page >= postsMeta?.last_page) return;
    let url = "";
    // Profile / user-scoped view must win over feed (`isPost`), or pagination hits the global feed.
    if (showIndividualPost) {
      url = `user/user-posts`;
    } else if (isPost) {
      url = `user/post-feed/posts`;
    } else {
      url = `user/group/posts/${selectedGroup}`;
    }
    setLoadingPosts(true);
    try {
      const nextPage = postsMeta.current_page + 1;

      const params = {
        per_page: 10,
        page: nextPage,
      };

      if (showIndividualPost) {
        params.user_id = currentSelectedUserId;
        if (currentSelectedGroupId && currentSelectedGroupId !== 0) {
          params.group_id = currentSelectedGroupId;
        } else if (selectedGroup !== 0) {
          params.group_id = selectedGroup;
        }
      }

      const res = await api.get(url, { params });

      setLocalGroupDetails((prev) => ({
        ...prev,
        posts: [...prev.posts, ...res.data.data.posts],
        meta: {
          ...prev.meta,
          posts: res.data.data.meta,
        },
      }));
    } catch (error) {
      console.error("Error loading more posts:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const loadMoreMedia = async () => {
    const mediaMeta = localGroupDetails.meta.media;
    if (loadingPosts || mediaMeta?.current_page >= mediaMeta?.last_page) return;

    let url = "";
    if (showIndividualPost) {
      url = `user/user-posts/medias`;
    } else if (isPost) {
      url = `user/post-feed/medias`;
    } else {
      url = `user/group/medias/${selectedGroup}`;
    }
    setLoadingMedia(true);
    try {
      const nextPage = mediaMeta.current_page + 1;
      const params = {
        per_page: 10,
        page: nextPage,
      };

      if (showIndividualPost) {
        params.user_id = currentSelectedUserId;
        if (currentSelectedGroupId && currentSelectedGroupId !== 0) {
          params.group_id = currentSelectedGroupId;
        } else if (selectedGroup !== 0) {
          params.group_id = selectedGroup;
        }
      }

      const res = await api.get(url, { params });

      setLocalGroupDetails((prev) => ({
        ...prev,
        media: [...prev.media, ...res.data.data.medias],
        meta: {
          ...prev.meta,
          media: res.data.data.meta,
        },
      }));
    } catch (error) {
      console.error("Error loading more media:", error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const loadMoreSaved = async () => {
    const postsMeta = localGroupDetails.meta.savedPosts;
    if (loadingPosts || postsMeta?.current_page >= postsMeta?.last_page) return;

    let url = "";
    if (isPost) {
      url = `user/post-feed/saved-posts`;
    } else {
      url = `user/group/saved-posts/${selectedGroup}`;
    }
    setLoadingSaved(true);
    try {
      const nextPage = postsMeta.current_page + 1;
      const res = await api.get(url, {
        params: { per_page: 10, page: nextPage },
      });

      setLocalGroupDetails((prev) => ({
        ...prev,
        savedPosts: [...prev.savedPosts, ...res.data.data.savedPosts],
        meta: {
          ...prev.meta,
          savedPosts: res.data.data.meta,
        },
      }));
    } catch (error) {
      console.error("Error loading more saved posts:", error);
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleScroll = (e) => {
    const el = e.target;

    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      const postsMeta = localGroupDetails.meta.posts;
      const savedMeta = localGroupDetails.meta.savedPosts;

      if (activeTab === "posts") {
        if (useNewApiForUserPosts && showIndividualPost) {
          if (
            hasNextUserPostsPage &&
            !isFetchingNextUserPostsPage &&
            !loadingPosts
          ) {
            fetchNextUserPostsPage();
          }
        } else if (
          !loadingPosts &&
          postsMeta?.current_page < postsMeta?.last_page
        ) {
          loadMorePosts();
        }
      } else if (
        activeTab === "saved" &&
        !loadingSaved &&
        savedMeta?.current_page < savedMeta?.last_page
      ) {
        loadMoreSaved();
      }
    }
  };

  const handlePostCreated = (newPost) => {
    setLocalGroupDetails((prev) => {
      const currentPosts = prev?.posts || [];
      return {
        ...prev,
        posts: [newPost, ...currentPosts],
      };
    });
    onPost(newPost);
  };

  const handlePostUpdated = (updatedPost) => {
    setLocalGroupDetails((prev) => {
      const updatedPosts = prev.posts.map((post) =>
        post.id === updatedPost.id ? { ...post, ...updatedPost } : post,
      );
      const updatedSavedPosts = prev.savedPosts.map((post) =>
        post.id === updatedPost.id ? { ...post, ...updatedPost } : post,
      );
      return {
        ...prev,
        posts: updatedPosts,
        savedPosts: updatedSavedPosts,
      };
    });
  };

  //save post
  const handlePostSaved = (postId, isSaved) => {
    setLocalGroupDetails((prev) => {
      const updatedPosts = prev.posts.map((post) =>
        post.id === postId ? { ...post, is_bookmarked: isSaved } : post,
      );

      let updatedSavedPosts;
      if (isSaved) {
        // Add to saved posts if not already there
        const postToAdd = prev.posts.find((p) => p.id === postId);
        if (postToAdd && !prev.savedPosts.some((p) => p.id === postId)) {
          updatedSavedPosts = [
            ...prev.savedPosts,
            { ...postToAdd, is_bookmarked: true },
          ];
        } else {
          updatedSavedPosts = prev.savedPosts;
        }
      } else {
        // Remove from saved posts
        updatedSavedPosts = prev.savedPosts.filter((p) => p.id !== postId);
      }

      return {
        ...prev,
        posts: updatedPosts,
        savedPosts: updatedSavedPosts,
      };
    });
  };

  const handleShowIndividualPost = (val) => {
    setShowIndividualPost(val);
    onShowIndividualPostChange?.(val);
  };

  const handlePostHide = (postId) => {
    setLocalGroupDetails((prev) => {
      const updatedPosts = prev.posts.filter((post) => post.id !== postId);
      const updatedSavedPosts = prev.savedPosts.filter(
        (post) => post.id !== postId,
      );

      return {
        ...prev,
        posts: updatedPosts,
        savedPosts: updatedSavedPosts,
      };
    });
  };

  const handleUserclicked = async (
    userId,
    member,
    showPostOwnerInfoFlag = false,
    providedGroupId = undefined,
  ) => {
    try {
      const inferredMemberFromPosts = (localGroupDetails?.posts || []).reduce(
        (acc, post) => {
          if (acc) return acc;
          if (post?.created_by?.id === userId) return post.created_by;
          const taggedUser = (post?.tagged_users || []).find(
            (u) => u?.id === userId,
          );
          return taggedUser || null;
        },
        null,
      );
      const inferredMemberFromGroup = (
        localGroupDetails?.about?.members || []
      ).find((m) => m?.user?.id === userId)?.user;
      let resolvedMember =
        member?.user ||
        member ||
        inferredMemberFromPosts ||
        inferredMemberFromGroup ||
        null;

      // Feed clicks can provide only userId + groupId. In that case, fetch group
      // members and resolve clicked user to avoid empty profile header.
      if (!resolvedMember && providedGroupId) {
        try {
          const aboutRes = await api.get(`user/group/about/${providedGroupId}`);
          const aboutMembers = aboutRes?.data?.data?.about?.members || [];
          const matchedMember =
            aboutMembers.find((m) => m?.user?.id === userId)?.user || null;
          if (matchedMember) {
            resolvedMember = matchedMember;
          }
        } catch (aboutError) {
          console.error(
            "[TribeContent][handleUserclicked] failed about fallback",
            aboutError,
          );
        }
      }

      setLoadingMedia(true);
      setActiveTab("posts");
      setCurrentSelectedUserId(userId);
      const derivedGroupId =
        providedGroupId ??
        resolvedMember?.group?.id ??
        resolvedMember?.group_id ??
        resolvedMember?.pivot?.group_id;
      const effectiveGroupId = derivedGroupId || selectedGroup;
      setCurrentSelectedGroupId(effectiveGroupId);

      // If username is clicked from feed but user details are unavailable,
      // fall back to group-style flow to avoid null header values.
      const useGroupStyleFlow =
        !showPostOwnerInfoFlag || (!resolvedMember && selectedGroup === 0);

      // New API flow is used only when we can render post owner info safely.
      setUseNewApiForUserPosts(!useGroupStyleFlow);

      if (!useGroupStyleFlow) {
        // New API will be called via useGetUserPosts hook
        // We only need to fetch media here
        setLoadingPosts(true);
        setLoadingUserPosts(true);

        const mediaParams = {
          user_id: userId,
          per_page: 20,
          page: 1,
        };
        if (effectiveGroupId !== 0) {
          mediaParams.group_id = effectiveGroupId;
        }

        try {
          const mediaRes = await api.get(`user/user-posts/medias`, {
            params: mediaParams,
          });

          setLocalGroupDetails((prev) => ({
            ...prev,
            media: mediaRes.data.data.medias,
            meta: {
              ...prev.meta,
              media: mediaRes.data.data.meta,
            },
          }));
        } catch (mediaError) {
          // Allow navigation to user profile even if media endpoint fails.
          console.error("Error fetching user media:", mediaError);
          setLocalGroupDetails((prev) => ({
            ...prev,
            media: [],
            meta: {
              ...prev.meta,
              media: {},
            },
          }));
        }

        setShowPostOwnerInfo(true);
        // Post owner info will be set from hook data
        if (resolvedMember) {
          setPostOwnerInfo(resolvedMember);
        }
      } else {
        // Old API - for member clicks from About section
        setLoadingPosts(true);
        setLoadingUserPosts(true);

        const postsParams = {
          user_id: userId,
          per_page: 10,
          page: 1,
        };
        const mediaParams = {
          user_id: userId,
          per_page: 20,
          page: 1,
        };

        if (effectiveGroupId !== 0) {
          postsParams.group_id = effectiveGroupId;
          mediaParams.group_id = effectiveGroupId;
        }

        const [postsRes, mediaRes] = await Promise.all([
          api.get(`user/user-posts`, {
            params: postsParams,
          }),
          api.get(`user/user-posts/medias`, {
            params: mediaParams,
          }),
        ]);

        setLocalGroupDetails((prev) => ({
          ...prev,
          posts: postsRes.data.data.posts,
          media: mediaRes.data.data.medias,
          meta: {
            ...prev.meta,
            posts: postsRes.data.data.meta,
            media: mediaRes.data.data.meta,
          },
        }));

        setShowPostOwnerInfo(false);
        setPostOwnerInfo(null);
      }

      setCurrentMember(resolvedMember);
      handleShowIndividualPost(true);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error("Error fetching user-specific posts/media:", error);
    } finally {
      setLoadingPosts(false);
      setLoadingMedia(false);
      setLoadingUserPosts(false);
    }
  };

  useEffect(() => {
    if (!memberProfileRequest?.member?.user?.id) return;

    handleUserclicked(
      memberProfileRequest.member.user.id,
      memberProfileRequest.member,
    );
    onMemberProfileRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberProfileRequest?.requestId]);

  // Update local state when user posts data changes from the new API hook
  useEffect(() => {
    if (
      userPostsData &&
      useNewApiForUserPosts &&
      currentSelectedUserId &&
      showIndividualPost
    ) {
      // Flatten all pages of posts
      const allPosts = userPostsData.pages.flatMap((page) => page.posts || []);

      // Get meta from the last page
      const lastPage = userPostsData.pages[userPostsData.pages.length - 1];
      const meta = lastPage?.meta || {};

      setLocalGroupDetails((prev) => ({
        ...prev,
        posts: allPosts,
        meta: {
          ...prev.meta,
          posts: meta,
        },
      }));

      // Set post owner info from first post if not already set
      if (showPostOwnerInfo && allPosts.length > 0 && !postOwnerInfo) {
        setPostOwnerInfo(allPosts[0].created_by);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userPostsData,
    useNewApiForUserPosts,
    currentSelectedUserId,
    showIndividualPost,
  ]);

  // Update loading state based on hook when using new API
  useEffect(() => {
    if (useNewApiForUserPosts) {
      setLoadingUserPosts(isLoadingUserPostsFromHook);
      setLoadingPosts(isLoadingUserPostsFromHook);
    }
  }, [isLoadingUserPostsFromHook, useNewApiForUserPosts]);

  useEffect(() => {
    if (
      selectedPostId &&
      localGroupDetails?.posts &&
      !hasScrolledToPost.current
    ) {
      // Switch to posts tab if not already there
      if (activeTab !== "posts") {
        setActiveTab("posts");
      }

      // Wait a bit for the DOM to render
      setTimeout(() => {
        const postElement = document.getElementById(`post-${selectedPostId}`);

        if (postElement) {
          // Scroll to the post
          postElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Apply highlight
          postElement.style.transition = "background-color 0.5s ease";
          postElement.style.backgroundColor = "rgba(42, 64, 82, 0.5)"; // Use a subtle highlight color matching the dark theme

          // Remove highlight after 3 seconds
          setTimeout(() => {
            postElement.style.backgroundColor = "";
          }, 3000);

          // Mark as scrolled
          hasScrolledToPost.current = true;
        } else {
        }
      }, 500); // Give time for posts to render
    }
  }, [selectedPostId, localGroupDetails?.posts, activeTab]);

  //ADD: Reset scroll flag when selectedGroup changes
  useEffect(() => {
    hasScrolledToPost.current = false;
  }, [selectedGroup]);

  // Update local state when groupDetails changes
  useEffect(() => {
    setLocalGroupDetails(groupDetails);
  }, [groupDetails]);

  useEffect(() => {
    setActiveTab("posts");
  }, [selectedGroup]);

  const profileDisplayName =
    postOwnerInfo?.full_name ||
    postOwnerInfo?.name ||
    (postOwnerInfo?.slug ? `@${postOwnerInfo.slug}` : "") ||
    currentMember?.user?.full_name ||
    currentMember?.user?.name ||
    (currentMember?.user?.slug ? `@${currentMember.user.slug}` : "") ||
    currentMember?.full_name ||
    currentMember?.name ||
    (currentMember?.slug ? `@${currentMember.slug}` : "") ||
    "";
  const profileOccupation =
    postOwnerInfo?.occupation ||
    postOwnerInfo?.job_title ||
    postOwnerInfo?.designation ||
    currentMember?.user?.occupation ||
    currentMember?.user?.job_title ||
    currentMember?.user?.designation ||
    currentMember?.occupation ||
    currentMember?.job_title ||
    currentMember?.designation ||
    "";
  const profileTitle =
    profileDisplayName && profileOccupation
      ? `${profileDisplayName} | ${profileOccupation}`
      : profileDisplayName;
  const selectedUserImage =
    postOwnerInfo?.image ||
    currentMember?.user?.image ||
    currentMember?.image ||
    "icons/james.svg";

  useEffect(() => {
    if (!showIndividualPost) return;
  }, [
    showIndividualPost,
    showPostOwnerInfo,
    currentSelectedUserId,
    postOwnerInfo,
    currentMember,
    profileDisplayName,
    profileOccupation,
    profileTitle,
    selectedUserImage,
  ]);

  // Return loading state if groupDetails is null
  if (!groupDetails || loadingUserPosts) {
    return (
      <div className="flex-grow-1 flex-column chats-contents active-chat">
        <div className="text-center py-5">
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        </div>
      </div>
    );
  }

  const renderSection = () => {
    switch (activeTab) {
      case "posts":
        return (
          <PostsSection
            isAdminOnly={isAdminOnly}
            isCurrentUserAdmin={isCurrentUserAdmin}
            isPost={isPost}
            posts={(localGroupDetails.posts || []).filter(
              (post) =>
                !post.scheduled_post ||
                post.scheduled_post.is_published === true,
            )}
            groupDetails={localGroupDetails}
            onPostCreated={handlePostCreated}
            userProfile={userProfile}
            handleScroll={handleScroll}
            hasMore={
              useNewApiForUserPosts && showIndividualPost
                ? !!hasNextUserPostsPage
                : (localGroupDetails?.meta?.posts?.current_page || 0) <
                  (localGroupDetails?.meta?.posts?.last_page || 0)
            }
            isLoading={
              useNewApiForUserPosts && showIndividualPost
                ? loadingPosts || isFetchingNextUserPostsPage
                : loadingPosts
            }
            onLoadMore={
              useNewApiForUserPosts && showIndividualPost
                ? fetchNextUserPostsPage
                : loadMorePosts
            }
            scrollRootRef={scrollContainerRef}
            onPostSaved={handlePostSaved}
            onPostHide={handlePostHide}
            showCreateTask={!showIndividualPost} // value is set as showIndividualPost to show createPost only in feed and not in profile page
            members={localGroupDetails?.about?.members || []}
            onPostUpdated={handlePostUpdated}
            selectedPostId={selectedPostId}
            onUserClicked={handleUserclicked}
            showPostOwnerInfo={showPostOwnerInfo}
            postOwnerInfo={postOwnerInfo}
            onPost={onPost}
            // selectedCommentId={selectedCommentId}
            // openCommentBox={openCommentBox}
            onGroupClicked={handleGroupClicked}
            fallbackGroupId={currentSelectedGroupId || selectedGroup}
          />
        );
      case "scheduled":
        return (
          <PostsSection
            isAdminOnly={isAdminOnly}
            isCurrentUserAdmin={isCurrentUserAdmin}
            isPost={isPost}
            posts={scheduledPosts}
            groupDetails={localGroupDetails}
            onPostCreated={handlePostCreated}
            userProfile={userProfile}
            handleScroll={handleScroll}
            hasMore={!!hasNextScheduledPostsPage}
            isLoading={
              isLoadingScheduledPosts || isFetchingNextScheduledPostsPage
            }
            onLoadMore={fetchNextScheduledPostsPage}
            scrollRootRef={scrollContainerRef}
            onPostSaved={handlePostSaved}
            onPostHide={handlePostHide}
            showCreateTask={false}
            members={localGroupDetails?.about?.members || []}
            onPostUpdated={handlePostUpdated}
            selectedPostId={selectedPostId}
            onUserClicked={handleUserclicked}
            showPostOwnerInfo={showPostOwnerInfo}
            postOwnerInfo={postOwnerInfo}
            onPost={onPost}
            onGroupClicked={handleGroupClicked}
            fallbackGroupId={currentSelectedGroupId || selectedGroup}
          />
        );
      case "about":
        return isPost ? null : (
          <AboutSection
            about={localGroupDetails?.about}
            members={localGroupDetails?.about?.members || []}
            handleUserclickedOnAbout={handleUserclicked}
          />
        );
      case "media":
        return (
          <MediaSection
            media={localGroupDetails?.media || []}
            onLoadMore={loadMoreMedia}
            hasMore={
              localGroupDetails?.meta?.media?.current_page <
              localGroupDetails?.meta?.media?.last_page
            }
            isLoading={loadingMedia}
          />
        );
      case "saved":
        return (
          <SavedSection
            savedPosts={localGroupDetails?.savedPosts || []}
            onScroll={handleScroll}
            hasMore={localGroupDetails?.meta?.savedPosts?.has_next_page}
            isLoading={loadingSaved}
            onPostSaved={handlePostSaved}
            userProfile={userProfile}
            members={localGroupDetails?.about?.members || []}
            onPostUpdated={handlePostUpdated}
            onPostHide={handlePostHide}
            onUserClicked={handleUserclicked}
            onGroupClicked={handleGroupClicked}
            onPost={onPost}
          />
        );
      default:
        return (
          <div style={{ color: "#fff", padding: "20px" }}>Coming Soon</div>
        );
    }
  };

  return (
    <div className="flex-grow-1 flex-column chats-contents active-chat">
      <div
        className="d-flex flex-column flex-grow-1 no-scrollbar "
        style={{ height: "0px", minHeight: "0px" }}
        onScroll={handleScroll}
        ref={scrollContainerRef}
      >
        {/* Header */}
        <div
          className="d-flex justify-content-between chats-card-content align-items-center"
          style={{ background: "#121e29" }}
        >
          <div className="d-flex align-items-center gap-2">
            {showIndividualPost && (
              <div className="">
                <img
                  style={{ cursor: "pointer" }}
                  src="/icons/back-arrow.svg"
                  alt="Back Button"
                  onClick={() => {
                    setLocalGroupDetails(groupDetails);
                    handleShowIndividualPost(false);
                    setActiveTab("posts");
                    setCurrentMember(null);
                    setCurrentSelectedUserId(null);
                    setCurrentSelectedGroupId(null);
                    setShowPostOwnerInfo(false);
                    setPostOwnerInfo(null);
                  }}
                />
              </div>
            )}

            <div className="d-flex align-items-center">
              {(!isPost || showIndividualPost) && (
                <div style={{ position: "relative" }}>
                  <img
                    className="chat-profile-self-img"
                    src={
                      showIndividualPost
                        ? showPostOwnerInfo
                          ? selectedUserImage
                          : selectedUserImage
                        : groupDetails?.about?.image || "icons/james.svg"
                    }
                    alt="Group"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                  {/* User level badge - only show when showing post owner info */}
                  {showIndividualPost &&
                    showPostOwnerInfo &&
                    postOwnerInfo?.user_level && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0px",
                          right: "-5px",
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: "#008CFF",
                          border: "2px solid #fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "10px",
                          fontWeight: "bold",
                        }}
                      >
                        {postOwnerInfo.user_level}
                      </div>
                    )}
                </div>
              )}
              <div
                className="d-flex flex-column"
                style={{ marginLeft: "16px" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="text-heading-h3"
                    style={{
                      color: "#d0d8df",
                      fontSize: "22px",
                      gap: "8px",
                    }}
                  >
                    {isPost && !showIndividualPost
                      ? "Feed"
                      : showIndividualPost
                        ? profileTitle
                        : groupDetails.about?.name}
                  </span>
                </div>

                {/* Member since and Coach badge section - appears below name/occupation */}
                {showIndividualPost && showPostOwnerInfo && (
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ marginTop: "4px" }}
                  >
                    {postOwnerInfo?.created_at && (
                      <span
                        className="text-body-b3"
                        style={{
                          color: "#d0d8df",
                          fontSize: "14px",
                        }}
                      >
                        Member since{" "}
                        {formatMemberSinceDate(postOwnerInfo?.created_at)}
                      </span>
                    )}
                    {postOwnerInfo?.is_coach && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          backgroundColor: "#008CFF",
                          color: "#ffffff",
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Coach
                      </span>
                    )}
                  </div>
                )}

                {isPost && !showIndividualPost ? (
                  <span style={{ color: "#d0d8df" }}>
                    Discover post from across your organisation
                  </span>
                ) : showIndividualPost ? (
                  showPostOwnerInfo && postOwnerInfo?.membership_level ? (
                    <span style={{ color: "#d0d8df" }}>
                      {postOwnerInfo.membership_level}
                    </span>
                  ) : null
                ) : (
                  <span style={{ color: "#d0d8df" }}>
                    {groupDetails.about?.access_type || "Group"}
                  </span>
                )}
              </div>
            </div>
          </div>

          {!isPost && !showIndividualPost && (
            <div className="chat-profile-image-user">
              {groupDetails?.about?.members?.some((m) => m.user.image) ? (
                <div style={{ display: "flex" }}>
                  {groupDetails?.about?.members
                    .filter((m) => m.user.image)
                    .slice(0, 3)
                    .map((member, index) => (
                      <img
                        key={index}
                        src={member.user.image}
                        alt={member.user.full_name}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginLeft: index !== 0 ? "-10px" : "0px",
                          border: "2px solid #1C2A37",
                        }}
                      />
                    ))}
                </div>
              ) : (
                <img
                  src="images/group-members.png"
                  alt="members"
                  style={{ height: "21px", width: "51px" }}
                />
              )}

              <span
                className="text-body-b2 dwn-text"
                style={{ color: "#b3ddff", textDecoration: "underline" }}
              >
                {groupDetails.about?.member_count || 0} Members
              </span>
            </div>
          )}
        </div>

        {/* Navigation Buttons  */}
        <div
          className="d-flex gap-2 gap-md-3 text-body-b4 chat-btn-container"
          style={{
            marginBottom: "32px",
            marginTop: "21px",
            marginLeft: "24px",
          }}
        >
          {(!isPost || showIndividualPost) &&
            (showIndividualPost
              ? ["posts", "media"]
              : ["posts", "scheduled", "about", "media", "saved"]
            )
              .filter((tab) => !(isPost && tab === "about"))
              .map((tab) => (
                <button
                  key={tab}
                  className={`btn-1 btn-1-basic chat-btn-1-basic ${activeTab === tab ? "active" : ""}`}
                  style={{
                    height: "30px",
                    whiteSpace: "nowrap",
                    backgroundColor: activeTab === tab ? "#fff" : "transparent",
                    color: activeTab === tab ? "#000" : "#fff",
                    borderRadius: "50px",
                    border: "1px solid #ccc",
                    padding: "6px 20px 6px 20px",
                  }}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
        </div>

        {/* Dynamic Content */}
        {renderSection()}
      </div>
    </div>
  );
};

export default TribeContent;
